/**
 * Prepass step of the selection wizard.
 *
 * The prepass is a sample of an exploration on the selection's laser and
 * substrate, marked beneath every palette sample that has no prepass of its
 * own (repeatability plates, palette plate, logo reproductions). Here the
 * user shortlists candidates, marks the palette once per candidate on one
 * plate (every block bedded on its candidate, labelled with the first three
 * and the last three characters of its sample id), compares them and picks
 * the final prepass at the end of the page. Next stores the
 * choice on the selection and opens the Spatial Repeatability Measurement;
 * for a manual selection, whose palette is already picked, Next finishes the
 * selection instead.
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    FormControlLabel,
    IconButton,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";
import axios from "axios";
import NavBarBottom from "../../components/NavBarBottom";
import ExplorationPageShell from "../../components/ExplorationPageShell";
import LaserMarkPanel from "../../components/LaserMarkPanel";
import MarkingTimeChip from "../../components/MarkingTimeChip";
import LoadingComponent from "../../components/LoadingComponent";
import PrepassSamplePicker, { designSummary, measurementSummary } from "../../components/PrepassSamplePicker";
import { MONO_FONT, Swatch, markedInLabel, shortSampleId } from "../../components/ManualPicksPanel";
import {
    isManualSelection, selectionLockedRoute, selectionSidebar,
} from "../../helper/selectionFlow";
import { useSelectionParams } from "../../helper/entityRoutes";
import { downloadBlob } from "../../helper/markingPreviewWindow";

const LAYOUT_DEBOUNCE_MS = 300;
const PREVIEW_WIDTH_PX = 520;

const errorText = (err, fallback) => err?.response?.data?.error || fallback;

/** Blob-typed requests deliver error bodies as a Blob; decode {error}. */
async function requestErrorDetail(error) {
    const data = error?.response?.data;
    if (data instanceof Blob) {
        try {
            return JSON.parse(await data.text()).error || null;
        } catch (parseError) {
            return null;
        }
    }
    return data?.error || null;
}

/** The two text rows above a candidate's block on the plate: the first
 *  three and the last three characters of its sample id ("fb6" over "c70"). */
export function candidateLabelLines(sampleId) {
    const text = String(sampleId);
    return [text.slice(0, 3), text.slice(-3)];
}

/** "fb6…c70": the block's label on one line, as the lists name a candidate. */
export function candidateLabel(sampleId) {
    return candidateLabelLines(sampleId).join("…");
}

/** The candidate plate as the layout endpoint describes it: the plate, its
 *  usable area and one labelled block per candidate, to scale. */
export function CandidatePlatePreview({ layout }) {
    if (!layout?.plate_mm) return null;
    const plate = layout.plate_mm;
    const scale = PREVIEW_WIDTH_PX / plate.width;
    const height = Math.max(plate.height * scale, 40);
    const px = (mm) => mm * scale;
    const rel = (box) => ({ x: px(box.x - plate.x), y: px(box.y - plate.y), w: px(box.width), h: px(box.height) });
    const usable = rel(layout.usable_mm);
    return (
        <svg
            data-testid="candidate-plate-preview"
            width={PREVIEW_WIDTH_PX}
            height={height}
            viewBox={`0 0 ${PREVIEW_WIDTH_PX} ${height}`}
            style={{ maxWidth: "100%", display: "block", background: "#f8fafc", borderRadius: 8 }}
        >
            <rect x={0.5} y={0.5} width={PREVIEW_WIDTH_PX - 1} height={height - 1} fill="none" stroke="#94a3b8" />
            <rect x={usable.x} y={usable.y} width={usable.w} height={usable.h} fill="none" stroke="#cbd5e1" strokeDasharray="4 3" />
            {layout.blocks.map((block) => {
                const label = rel(block.label_mm);
                const body = rel(block.block_mm);
                const lines = block.label_lines || candidateLabelLines(block.sample_id);
                const lineHeight = label.h / lines.length;
                const fontSize = Math.max(Math.min(lineHeight * 0.85, 12), 7);
                return (
                    <g key={block.sample_id}>
                        <rect x={body.x} y={body.y} width={body.w} height={body.h} fill="#dbeafe" stroke="#4C7EFF" />
                        {lines.map((line, row) => (
                            <text
                                key={row}
                                x={label.x + 2}
                                y={label.y + (row + 1) * lineHeight - 1}
                                fontSize={fontSize}
                                fontFamily="monospace"
                                fill="#1e293b"
                            >
                                {line}
                            </text>
                        ))}
                    </g>
                );
            })}
        </svg>
    );
}

export default function ColorSelectionPrepass() {
    const navigate = useNavigate();
    const { selectionId, selectionType: urlType, stepRoute } = useSelectionParams();

    const [selection, setSelection] = useState(null);
    const [pageError, setPageError] = useState(null);
    const [candidates, setCandidates] = useState([]);
    const [maxCandidates, setMaxCandidates] = useState(8);
    const [candidateError, setCandidateError] = useState(null);
    const [candidatesBusy, setCandidatesBusy] = useState(false);
    /** The final prepass: a candidate's sample id, or null for no prepass. */
    const [choice, setChoice] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);

    const [patchSize, setPatchSize] = useState("");
    const [markCrosses, setMarkCrosses] = useState(true);
    const [layout, setLayout] = useState(null);
    const [layoutError, setLayoutError] = useState(null);
    const [markingBusy, setMarkingBusy] = useState(false);
    const [markingError, setMarkingError] = useState(null);

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [finishing, setFinishing] = useState(false);
    const [finishError, setFinishError] = useState(null);
    const layoutRequest = useRef(0);

    const selectionType = selection?.selection_type || urlType;
    const manual = isManualSelection(selectionType);
    const backStep = manual ? "search" : "composition";
    const nextStep = manual ? "overview" : "spatial";
    const sampleCount = Array.isArray(selection?.selected_samples) ? selection.selected_samples.length : null;
    const candidateIds = candidates.map((row) => row.sample_id);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const response = await axios.get(`api/color/selection/${selectionId}/`);
                const row = response.data || {};
                const locked = selectionLockedRoute(selectionId, row.status, row.selection_type || urlType);
                if (locked) {
                    navigate(locked, { replace: true });
                    return;
                }
                if (cancelled) return;
                setSelection(row);
                setChoice(row.prepass_sample ? String(row.prepass_sample) : null);
                const list = await axios.get(`api/selection/prepass/candidates?selectionId=${selectionId}`);
                if (cancelled) return;
                setCandidates(list.data.candidates || []);
                if (list.data.max_candidates) setMaxCandidates(list.data.max_candidates);
            } catch (err) {
                if (!cancelled) setPageError(errorText(err, "Loading the selection failed."));
            }
        };
        load();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectionId]);

    const patchSizeValue = Number(patchSize);
    const patchSizeTyped = String(patchSize).trim() !== "";
    const patchSizePositive = patchSizeTyped && Number.isFinite(patchSizeValue) && patchSizeValue > 0;

    // The candidate plate's layout follows the shortlist and the options.
    useEffect(() => {
        if (!selection || candidates.length === 0) {
            setLayout(null);
            setLayoutError(null);
            return undefined;
        }
        if (patchSizeTyped && !patchSizePositive) return undefined;
        const request = layoutRequest.current + 1;
        layoutRequest.current = request;
        const timer = setTimeout(async () => {
            try {
                const response = await axios.post("api/selection/prepass/marking/layout", {
                    selectionId,
                    candidateSampleIds: candidateIds,
                    markCrosses,
                    ...(patchSizePositive ? { patchSize: patchSizeValue } : {}),
                });
                if (layoutRequest.current !== request) return;
                const next = response.data.layout;
                setLayout(next);
                setLayoutError(null);
                if (!patchSizeTyped && next?.patch_size != null) setPatchSize(String(next.patch_size));
            } catch (err) {
                if (layoutRequest.current !== request) return;
                setLayout(null);
                setLayoutError(errorText(err, "Laying out the candidate plate failed."));
            }
        }, LAYOUT_DEBOUNCE_MS);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selection, candidateIds.join(","), patchSize, markCrosses]);

    const storeCandidates = async (ids) => {
        setCandidatesBusy(true);
        setCandidateError(null);
        try {
            const response = await axios.post("api/selection/prepass/candidates", {
                selectionId, candidateSampleIds: ids,
            });
            setCandidates(response.data.candidates || []);
        } catch (err) {
            setCandidateError(errorText(err, "Updating the candidates failed."));
        } finally {
            setCandidatesBusy(false);
        }
    };

    const addCandidate = (row) => {
        if (!row?.sample_id) return;
        const id = String(row.sample_id);
        if (candidateIds.includes(id)) return;
        if (candidateIds.length >= maxCandidates) {
            setCandidateError(`At most ${maxCandidates} candidates can be compared on one plate.`);
            return;
        }
        storeCandidates([...candidateIds, id]);
    };

    const removeCandidate = (id) => {
        if (choice === id) setChoice(null);
        storeCandidates(candidateIds.filter((other) => other !== id));
    };

    const requestMarking = async () => {
        if (!patchSizePositive) throw new Error("Enter a patch size.");
        if (layout && !layout.fits) throw new Error(layout.error || "The candidate plate does not fit.");
        try {
            const response = await axios.post(
                "api/selection/prepass/marking/file",
                { selectionId, candidateSampleIds: candidateIds, patchSize: patchSizeValue, markCrosses },
                { responseType: "blob", timeout: 600000 },
            );
            return { blob: response.data, filename: `prepass_candidates_${candidateIds.length}.json` };
        } catch (error) {
            console.error("Error generating the candidate marking file:", error);
            throw new Error((await requestErrorDetail(error)) || "Generating the marking file failed.");
        }
    };

    const downloadMarking = async () => {
        setMarkingBusy(true);
        setMarkingError(null);
        try {
            const { blob, filename } = await requestMarking();
            downloadBlob(blob, filename);
        } catch (error) {
            setMarkingError(error.message);
        } finally {
            setMarkingBusy(false);
        }
    };

    const savePrepass = async () => {
        await axios.patch(`api/color/selection/${selectionId}/`, { prepass_sample: choice });
    };

    const onNext = async (navigatePageName) => {
        setSaving(true);
        setSaveError(null);
        try {
            await savePrepass();
            if (manual) {
                setFinishError(null);
                setConfirmOpen(true);
                return;
            }
            navigate(navigatePageName);
        } catch (err) {
            setSaveError(errorText(err, "Storing the prepass failed."));
        } finally {
            setSaving(false);
        }
    };

    const finish = async () => {
        setFinishing(true);
        setFinishError(null);
        try {
            await axios.post("api/selection/manual/finalize", { selectionId });
            setConfirmOpen(false);
            navigate(stepRoute("overview", selectionType));
        } catch (err) {
            setFinishError(errorText(err, "Finishing the selection failed."));
        } finally {
            setFinishing(false);
        }
    };

    const ready = Boolean(selection);
    const canMark = candidates.length > 0 && patchSizePositive && Boolean(layout?.fits);
    const choiceRow = choice ? candidates.find((row) => row.sample_id === choice) : null;

    return (
        <Stack alignItems="center" sx={{ mt: "2%", mb: 4, width: "100%" }} spacing={2}>
            <ExplorationPageShell
                title="Prepass"
                subtitle="Shortlist prepass candidates, mark the palette once per candidate to compare them, then pick the final prepass."
                stepSidebar={selectionSidebar("prepass", sampleCount, selectionType)}
                footerBelowPaper={
                    <Box sx={{ width: "100%" }}>
                        <NavBarBottom
                            variant="embedded"
                            returnLabel="Back"
                            prevPage={stepRoute(backStep, selectionType)}
                            nextPage={stepRoute(nextStep, selectionType)}
                            nextLabel={manual ? "Finish" : "Next"}
                            enableNextPage={!ready || saving || finishing || (manual && sampleCount === 0)}
                            nextLoading={saving}
                            nextPageClickFunc={onNext}
                        />
                    </Box>
                }
            >
                {pageError ? <Alert severity="error" sx={{ mb: 2 }}>{pageError}</Alert> : null}
                {!ready && !pageError ? <LoadingComponent message="Loading the selection..." /> : null}
                {ready ? (
                    <Stack spacing={3}>
                        <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                                Candidates
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                A prepass is marked beneath every palette sample that has no prepass of its own, on the repeatability plates, the palette plate and in logo reproductions. Add up to {maxCandidates} candidates of this laser and substrate.
                            </Typography>
                            {candidates.length > 0 ? (
                                <Stack spacing={1} sx={{ mb: 2, maxWidth: 640 }}>
                                    {candidates.map((row) => (
                                        <Box
                                            key={row.sample_id}
                                            data-testid="prepass-candidate-row"
                                            sx={{
                                                display: "flex", alignItems: "center", gap: 1.5, p: 1.25,
                                                borderRadius: 2, border: "1px solid", borderColor: "rgba(15, 23, 42, 0.1)",
                                            }}
                                        >
                                            <Chip size="small" label={candidateLabel(row.sample_id)} sx={{ fontFamily: MONO_FONT }} />
                                            <Swatch rgb={row.rgb} size={32} />
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography sx={{ fontFamily: MONO_FONT, fontSize: 13, fontWeight: 600, wordBreak: "break-all" }}>
                                                    {row.sample_id}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.4 }}>
                                                    {[row.exploration_name, markedInLabel(row.marked_in), measurementSummary(row)].filter(Boolean).join(" · ")}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.4 }}>
                                                    {designSummary(row.design)}
                                                </Typography>
                                            </Box>
                                            <IconButton
                                                size="small"
                                                aria-label={`Remove ${candidateLabel(row.sample_id)}`}
                                                onClick={() => removeCandidate(row.sample_id)}
                                                disabled={candidatesBusy}
                                            >
                                                <DeleteOutlineIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    ))}
                                </Stack>
                            ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    No candidates yet.
                                </Typography>
                            )}
                            {candidateError ? (
                                <Alert severity="error" sx={{ mb: 2, maxWidth: 640 }} onClose={() => setCandidateError(null)}>
                                    {candidateError}
                                </Alert>
                            ) : null}
                            <Box sx={{ maxWidth: 640, p: 2, borderRadius: 2, border: "1px solid", borderColor: "rgba(15, 23, 42, 0.1)" }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                    Add a candidate
                                </Typography>
                                <PrepassSamplePicker
                                    laserId={selection.laser_device}
                                    materialId={selection.material}
                                    value={null}
                                    onChange={addCandidate}
                                />
                            </Box>
                        </Box>

                        <Divider />

                        <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                                Compare on the plate
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                {sampleCount != null && sampleCount > 0
                                    ? `The palette of ${sampleCount} sample${sampleCount === 1 ? "" : "s"} is marked once per candidate, each block on its candidate and labelled with the first and last three characters of the candidate's sample ID.`
                                    : "The palette has no samples yet; compose or pick it first."}
                            </Typography>
                            <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap" useFlexGap>
                                <TextField
                                    id="prepass-patch-size"
                                    label="Patch size (mm)"
                                    type="number"
                                    size="small"
                                    value={patchSize}
                                    onChange={(event) => setPatchSize(event.target.value)}
                                    error={patchSizeTyped && (!patchSizePositive || Boolean(layout && !layout.fits))}
                                    helperText={layout?.max_patch_size != null ? `Max ${layout.max_patch_size} mm.` : " "}
                                    inputProps={{ min: 0.1, step: 0.1 }}
                                    sx={{ width: 180 }}
                                />
                                <FormControlLabel
                                    control={<Checkbox checked={markCrosses} onChange={(event) => setMarkCrosses(event.target.checked)} />}
                                    label="Mark crosses"
                                />
                            </Stack>
                            {layout ? (
                                <Box sx={{ mt: 1 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                                        {`${layout.blocks.length} block${layout.blocks.length === 1 ? "" : "s"} of ${layout.grid[0]} x ${layout.grid[1]} patches, ${layout.columns} per row.`}
                                    </Typography>
                                    <CandidatePlatePreview layout={layout} />
                                    {!layout.fits ? <Alert severity="warning" sx={{ mt: 1, maxWidth: 560 }}>{layout.error}</Alert> : null}
                                </Box>
                            ) : null}
                            {layoutError ? <Alert severity="error" sx={{ mt: 1, maxWidth: 560 }}>{layoutError}</Alert> : null}
                            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                                <Button
                                    variant="outlined"
                                    startIcon={markingBusy ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />}
                                    onClick={downloadMarking}
                                    disabled={markingBusy || !canMark}
                                    sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
                                >
                                    {markingBusy ? "Generating marking file..." : "Download marking file"}
                                </Button>
                                <MarkingTimeChip value={layout?.fits ? layout.marking_time : null} />
                            </Stack>
                            {markingError ? (
                                <Alert severity="error" sx={{ mt: 1, maxWidth: 560 }} onClose={() => setMarkingError(null)}>
                                    {markingError}
                                </Alert>
                            ) : null}
                            {canMark ? (
                                <Box sx={{ mt: 1 }}>
                                    <LaserMarkPanel getMarkingBlob={requestMarking} predictedMarkingTime={layout?.marking_time}>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                            Mark the candidate plate on the laser connected to this computer:
                                        </Typography>
                                    </LaserMarkPanel>
                                </Box>
                            ) : null}
                        </Box>

                        <Divider />

                        <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                                Final prepass
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {manual
                                    ? "Finish fixes the palette with this prepass."
                                    : "Next stores this prepass for the repeatability plates, the palette plate and logo reproductions."}
                            </Typography>
                            <RadioGroup
                                value={choice || ""}
                                onChange={(event) => setChoice(event.target.value || null)}
                            >
                                <FormControlLabel value="" control={<Radio />} label="No prepass" />
                                {candidates.map((row) => (
                                    <FormControlLabel
                                        key={row.sample_id}
                                        value={row.sample_id}
                                        control={<Radio />}
                                        label={`${candidateLabel(row.sample_id)} · ${row.exploration_name || ""}`}
                                    />
                                ))}
                            </RadioGroup>
                            {choice && !choiceRow ? (
                                <Typography variant="caption" color="text.secondary">
                                    The current prepass {shortSampleId(choice)} is not among the candidates; keeping it.
                                </Typography>
                            ) : null}
                            {saveError ? <Alert severity="error" sx={{ mt: 1, maxWidth: 560 }}>{saveError}</Alert> : null}
                        </Box>
                    </Stack>
                ) : null}
            </ExplorationPageShell>

            <Dialog open={confirmOpen} onClose={() => (finishing ? null : setConfirmOpen(false))}>
                <DialogTitle>Finish the selection?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {`The palette of ${sampleCount ?? 0} sample${sampleCount === 1 ? "" : "s"} is fixed${choice ? ` with prepass ${shortSampleId(choice)}` : " without a prepass"} and the selection becomes read-only.`}
                    </DialogContentText>
                    {finishError ? <Alert severity="error" sx={{ mt: 2 }}>{finishError}</Alert> : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)} disabled={finishing}>Cancel</Button>
                    <Button
                        variant="contained"
                        disableElevation
                        onClick={finish}
                        disabled={finishing}
                        startIcon={finishing ? <CircularProgress size={14} color="inherit" /> : null}
                    >
                        Finish
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}
